import { useState, useEffect, useContext } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash'

import {
	Dialog,
	DialogContent,
	DialogContentText,
	DialogTitle,
	DialogActions,
	Button,
	IconButton,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Close } from '@mui/icons-material'
import { makeStyles } from '@mui/styles'
import { IconThemeContext } from 'custom-components/context/IconThemesContext'

import DoformsRecordHistory from 'components/data/datagrid/DoFormsRecordHistory'
import DoformsPortal from '../../../../../custom-components/DoformsPortal'

import { ACTION_TYPE } from './helper/constants'
import { capitalizeFirstLetter } from '../../../../../components/data/dataHelpers'
import { performRecordAction } from 'components/data/datagrid/recordsHelper'

import { getViewRecords } from 'components/data/dataServices'
import {
	viewRecord,
	getPrintPreview,
	getRecordHistory,
	deleteRecord,
	executePostDispatchLink,
} from 'components/data/datagrid/recordsService'

const useStyles = makeStyles(() => ({
	dialog: {
		'& .MuiButton-root': {
			textTransform: 'none !important',
		},
	},
	dialogTitle: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		textAlign: 'center',
	},
	eventRenderer: {
		'& .MuiPaper-root': {
			marginTop: '-4px',
			minHeight: '25px',
			height: '96%',
		},
		'& .rs_event_item': {
			marginTop: '-4px',
		},
	},
	schedulerContainer: {
		'& .MuiPaper-root': {
			marginTop: '-3px',
			minHeight: '25px',
		},
		'& .rs__event__item': {
			marginTop: '2px',
			right: '3px',
		},
		'& .MuiPaper-root:has(> div:empty)': {
			display: 'none !important',
		},
	},
	buttonDialog: {
		'& .MuiButtonBase-root': {
			padding: '5px 1px !important',
		},
	},
	icon: (props) => ({
		color: props.color,
		'&:hover': {
			color: props.active.color,
			backgroundColor: 'transparent',
		},
	}),
	exitConfirmation: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		textAlign: 'center',
		'& .MuiButton-root': {
			textTransform: 'none !important',
		},
	},
	paper: {
		position: 'absolute',
		padding: '16px 32px 24px',
		borderRadius: '5px',
	},
	hidden: {
		display: 'none',
	},
}))

export const CustomEditor = (props) => {
	const { event: currentEvent, close, openCustomEditor, action, formSelected } = props
	const { environment } = useSelector((state) => state)
	const [t] = useTranslation()
	const [iframeSrc, setIframeSrc] = useState(null)

	const [historyData, setHistoryData] = useState({})
	const { iconTheme } = useContext(IconThemeContext)
	const classes = useStyles(iconTheme)

	const [actionLoading, setActionLoading] = useState(false)
	useEffect(() => {
		;(async () => {
			try {
				if (action === ACTION_TYPE.VIEW) {
					const record = {
						dispatchKey: currentEvent.dispatch.key,
						type: 'DISPATCH',
					}

					const res = await viewRecord(record, environment?.apiToken, formSelected)
					const iframeSrc = getPrintPreview(res.data, environment.apiToken)
					setIframeSrc(iframeSrc)
					return
				}

				if ([ACTION_TYPE.COPY, ACTION_TYPE.EDIT].includes(action)) {
					const record = {
						dispatchKey: currentEvent.dispatch.key,
						type: 'DISPATCH',
						sourceType: 'DISPATCH',
						key: currentEvent.dispatch.key,
					}
					const isDispathRequest =
						currentEvent.dispatch.systemStatus === 'Pending' ||
						currentEvent.dispatch.systemStatus === 'Acknowledged' ||
						currentEvent.dispatch.systemStatus === 'Recalled'

					const iframePromise = await performRecordAction(
						environment,
						record,
						action,
						formSelected,
						'forms',
						undefined,
						isDispathRequest
					)

					setIframeSrc(iframePromise)
					return
				}

				if ([ACTION_TYPE.HISTORY].includes(action)) {
					const record = {
						dispatchKey: currentEvent.dispatch.key,
						type: 'DISPATCH',
						sourceType: 'DISPATCH',
						key: currentEvent.dispatch.key,
					}
					const [viewResponse, recordHistoryResponse] = await Promise.all([
						getViewRecords(formSelected.key, formSelected.projectKey, '', environment.apiToken),
						getRecordHistory(record, environment.apiToken),
					])
					setHistoryData({
						record: recordHistoryResponse?.data,
						columns: viewResponse?.data?.columns,
					})
					return
				}
			} catch (err) {
				console.error(err)
				throw err
			}
		})()
	}, [currentEvent, action])

	async function handleRecall() {
		if (action !== ACTION_TYPE.RECALL) return

		try {
			setActionLoading(true)
			const record = {
				dispatchKey: currentEvent.dispatch.key,
				type: 'DISPATCH',
				sourceType: 'DISPATCH',
				key: currentEvent.dispatch.key,
			}
			const iframeSrc = await performRecordAction(
				environment,
				record,
				action,
				formSelected,
				'forms'
			)

			await executePostDispatchLink(iframeSrc, environment?.apiToken)
			setActionLoading(false)
		} catch (error) {
			console.log(error)
			throw error
		} finally {
			close()
		}
	}

	async function handleDelete() {
		if (action !== ACTION_TYPE.DELETE) return

		try {
			setActionLoading(true)
			const record = {
				dispatchKey: currentEvent.dispatch.key,
				type: 'DISPATCH',
				sourceType: 'DISPATCH',
				key: currentEvent.dispatch.key,
			}
			await deleteRecord(record, environment?.apiToken)
			setActionLoading(false)
		} catch (error) {
			console.log(error)
			throw error
		} finally {
			close()
		}
	}

	async function handleOK() {
		switch (action) {
			case ACTION_TYPE.DELETE:
				await handleDelete()
				return
			case ACTION_TYPE.RECALL:
				await handleRecall()
				break
			default:
				break
		}
	}

	return (
		<Dialog
			open={openCustomEditor}
			onClose={close}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			maxWidth={'md'}
			fullWidth
			className={classes.root}
			sx={{
				'& .MuiDialog-paper': {
					height: [ACTION_TYPE.DELETE, ACTION_TYPE.RECALL].includes(action)
						? '10% important'
						: '90% !important',
				},
			}}
		>
			<DialogTitle id="record-dialog-title" className={classes.dialogTitle}>
				{capitalizeFirstLetter(currentEvent?.title)}
				<IconButton onClick={close}>
					<Close />
				</IconButton>
			</DialogTitle>

			<DialogContent>
				<DialogContentText id="alert-dialog-description">
					{action === ACTION_TYPE.HISTORY && !isEmpty(historyData) && (
						<DoformsRecordHistory
							recordHistory={historyData?.record}
							environment={environment}
							tab={'form'}
							columns={historyData?.columns}
							onClose={close}
						/>
					)}
					{[ACTION_TYPE.VIEW, ACTION_TYPE.COPY, ACTION_TYPE.EDIT].includes(action) && iframeSrc && (
						<DoformsPortal
							iframeSrc={iframeSrc}
							onClose={close}
							onLoaded={() => {}}
							refreshData={() => {}}
						/>
					)}
					{action === ACTION_TYPE.DELETE && t('common:misc.areYouSureYouWantToDeleteThisRecord')}
					{action === ACTION_TYPE.RECALL ? (
						iframeSrc ? (
							<DoformsPortal
								iframeSrc={iframeSrc}
								onClose={close}
								onLoaded={() => {}}
								refreshData={() => {}}
							/>
						) : (
							t('common:misc.confirmRecallThisRecord')
						)
					) : null}
				</DialogContentText>
			</DialogContent>

			<DialogActions>
				<LoadingButton
					onClick={handleOK}
					className={classes.icon}
					loading={actionLoading}
					autoFocus
				>
					{t('common:misc.ok')}
				</LoadingButton>
				<Button onClick={close} className={classes.icon}>
					{t('common:misc.cancel')}
				</Button>
			</DialogActions>
		</Dialog>
	)
}
